import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../../components/ui/select";
import { 
  Truck, Zap, Crown, Check, CreditCard, Loader2, AlertCircle,
  CheckCircle2, ArrowLeft
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PLAN_ICONS = {
  starter: Truck,
  professional: Zap,
  enterprise: Crown
};

// PayPal component using direct script injection for maximum compatibility
function PayPalCheckoutButtons({ clientId, amount, currency = "EUR", onSuccess, onError, onCancel }) {
  const [status, setStatus] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const containerRef = useRef(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    // Remove any existing PayPal scripts
    const existingScripts = document.querySelectorAll('script[src*="paypal.com/sdk"]');
    existingScripts.forEach(script => script.remove());

    // Create and load PayPal script
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&components=buttons`;
    script.async = true;
    script.id = 'paypal-sdk-script';

    const handleLoad = () => {
      console.log('PayPal script loaded, window.paypal:', window.paypal);
      
      // Poll for PayPal.Buttons availability
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      
      const checkPayPal = setInterval(() => {
        attempts++;
        
        if (window.paypal && typeof window.paypal.Buttons === 'function') {
          clearInterval(checkPayPal);
          console.log('PayPal.Buttons available');
          renderButtons();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkPayPal);
          console.error('Timeout waiting for PayPal.Buttons');
          setStatus("error");
          setErrorMessage(
            "Timeout caricamento PayPal. " +
            "Questo può accadere se: 1) Il dominio non è autorizzato in PayPal Developer Dashboard, " +
            "2) Il Client ID non è valido, 3) Problemi di rete. " +
            "Contatta l'amministratore per verificare la configurazione PayPal."
          );
        }
      }, 100);
    };

    const handleError = (e) => {
      console.error('PayPal script failed to load:', e);
      setStatus("error");
      setErrorMessage("Impossibile caricare lo script PayPal. Verifica la connessione.");
    };

    const renderButtons = () => {
      if (!containerRef.current) {
        setStatus("error");
        setErrorMessage("Container non trovato");
        return;
      }

      try {
        window.paypal.Buttons({
          style: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal',
            height: 45
          },
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [{
                amount: {
                  value: amount.toFixed(2),
                  currency_code: currency
                }
              }]
            });
          },
          onApprove: async (data, actions) => {
            const details = await actions.order.capture();
            console.log('Payment captured:', details);
            onSuccess(details);
          },
          onError: (err) => {
            console.error('PayPal button error:', err);
            onError(err);
          },
          onCancel: () => {
            onCancel();
          }
        }).render(containerRef.current)
          .then(() => {
            console.log('PayPal buttons rendered');
            setStatus("ready");
          })
          .catch((err) => {
            console.error('Failed to render PayPal buttons:', err);
            setStatus("error");
            setErrorMessage("Errore nel rendering dei pulsanti PayPal.");
          });
      } catch (err) {
        console.error('PayPal buttons error:', err);
        setStatus("error");
        setErrorMessage(err.message);
      }
    };

    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);

    return () => {
      scriptLoaded.current = false;
      // Cleanup script on unmount
      const scriptEl = document.getElementById('paypal-sdk-script');
      if (scriptEl) scriptEl.remove();
    };
  }, [clientId, currency, amount, onSuccess, onError, onCancel]);

  if (status === "error") {
    return (
      <div className="text-center py-6">
        <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-500 font-medium">Errore PayPal</p>
        <p className="text-sm text-muted-foreground mt-2">{errorMessage}</p>
        <Button 
          variant="outline" 
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Riprova
        </Button>
      </div>
    );
  }

  return (
    <div>
      {status === "loading" && (
        <div className="text-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Caricamento PayPal...</p>
        </div>
      )}
      <div ref={containerRef} id="paypal-buttons-container" />
    </div>
  );
}

export default function CompanyCheckout() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1); // 1: plan, 2: billing, 3: payment
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState({});
  const [settings, setSettings] = useState({});
  const [company, setCompany] = useState(null);
  
  // Selected options
  const [selectedPlan, setSelectedPlan] = useState("professional");
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  
  // PayPal state
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalTransactionId, setPaypalTransactionId] = useState(null);
  const [showPaypalButtons, setShowPaypalButtons] = useState(false);
  const [paypalAmount, setPaypalAmount] = useState(0);
  
  // Billing info
  const [billingInfo, setBillingInfo] = useState({
    company_name: "",
    vat_number: "",
    address: "",
    city: "",
    province: "",
    cap: "",
    pec: "",
    sdi_code: ""
  });

  // Check for success/cancelled from Stripe redirect
  useEffect(() => {
    const status = searchParams.get("status");
    const sessionId = searchParams.get("session_id");
    
    if (status === "success" && sessionId) {
      checkPaymentStatus(sessionId);
    } else if (status === "cancelled") {
      toast.error("Pagamento annullato");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch plans
      const plansRes = await axios.get(`${API}/subscriptions/plans`);
      setPlans(plansRes.data.plans);
      
      // Fetch settings (for payment methods)
      try {
        const settingsRes = await axios.get(`${API}/admin/settings`, { headers, withCredentials: true });
        setSettings(settingsRes.data);
      } catch (e) {
        // Non-admin users cant access settings, thats ok
      }
      
      // Fetch company info
      if (user?.company_id) {
        const companyRes = await axios.get(`${API}/companies/${user.company_id}`, { headers, withCredentials: true });
        setCompany(companyRes.data);
        
        // Pre-fill billing info from company
        setBillingInfo({
          company_name: companyRes.data.name || "",
          vat_number: companyRes.data.vat_number || "",
          address: companyRes.data.address || "",
          city: companyRes.data.city || "",
          province: companyRes.data.province || "",
          cap: companyRes.data.cap || "",
          pec: companyRes.data.email || "",
          sdi_code: ""
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/checkout/status/${sessionId}`, { headers, withCredentials: true });
      
      if (res.data.status === "completed" || res.data.stripe_payment_status === "paid") {
        toast.success("Pagamento completato con successo!");
        navigate("/company/abbonamento");
      }
    } catch (error) {
      console.error("Error checking status:", error);
    }
  };

  const calculateTotal = () => {
    const plan = plans[selectedPlan];
    if (!plan) return 0;
    return plan.price * selectedMonths;
  };

  const handleCheckout = async () => {
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(`${API}/checkout/create`, {
        plan_id: selectedPlan,
        months: selectedMonths,
        payment_method: paymentMethod,
        billing_info: billingInfo,
        origin_url: window.location.origin
      }, { headers, withCredentials: true });
      
      if (paymentMethod === "stripe" && res.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = res.data.checkout_url;
      } else if (paymentMethod === "paypal") {
        // Show PayPal buttons
        if (res.data.paypal_client_id) {
          setPaypalClientId(res.data.paypal_client_id);
          setPaypalTransactionId(res.data.transaction_id);
          setPaypalAmount(res.data.amount);
          setShowPaypalButtons(true);
        } else {
          toast.error("PayPal non configurato. Contatta l'amministratore o usa Stripe.");
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Errore durante il checkout";
      toast.error(errorMsg);
      console.error("Checkout error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaypalApprove = async (data, actions) => {
    try {
      // Capture the order
      const details = await actions.order.capture();
      
      // Send to backend to complete subscription
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/checkout/paypal/capture`, {
        transaction_id: paypalTransactionId,
        paypal_order_id: details.id
      }, { headers, withCredentials: true });
      
      toast.success("Pagamento completato con successo!");
      navigate("/company/abbonamento?status=success");
    } catch (error) {
      console.error("PayPal error:", error);
      toast.error("Errore durante il pagamento PayPal");
    }
  };

  const handlePaypalError = (err) => {
    console.error("PayPal error:", err);
    toast.error("Errore PayPal. Riprova o usa un altro metodo di pagamento.");
    setShowPaypalButtons(false);
  };

  if (loading) {
    return (
      <DashboardLayout title="Checkout">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!user?.company_id) {
    return (
      <DashboardLayout title="Checkout">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-bold text-lg mb-2">Nessuna azienda registrata</h3>
            <p className="text-muted-foreground mb-4">
              Devi prima registrare la tua azienda per acquistare un abbonamento.
            </p>
            <Button onClick={() => navigate("/company/profilo")}>
              Registra Azienda
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Acquista Abbonamento">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="company-checkout">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}
              >
                {step > s ? <Check className="h-5 w-5" /> : s}
              </div>
              {s < 3 && <div className={`w-20 h-1 mx-2 ${step > s ? "bg-primary" : "bg-muted"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Plan */}
        {step === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Scegli il tuo Piano</CardTitle>
                <CardDescription>Seleziona il piano più adatto alle tue esigenze</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(plans).map(([planId, plan]) => {
                    const Icon = PLAN_ICONS[planId] || Truck;
                    const isSelected = selectedPlan === planId;
                    
                    return (
                      <div
                        key={planId}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => setSelectedPlan(planId)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className={`p-2 rounded ${isSelected ? "bg-primary" : "bg-muted"}`}>
                            <Icon className={`h-5 w-5 ${isSelected ? "text-primary-foreground" : "text-muted-foreground"}`} />
                          </div>
                          {isSelected && <CheckCircle2 className="h-5 w-5 text-primary" />}
                        </div>
                        <h3 className="font-bold text-lg">{plan.name}</h3>
                        <p className="text-2xl font-black font-mono mt-2">
                          {formatCurrency(plan.price)}
                          <span className="text-sm text-muted-foreground font-normal">/mese</span>
                        </p>
                        <ul className="mt-4 space-y-2">
                          {plan.features?.slice(0, 3).map((f, i) => (
                            <li key={i} className="text-sm flex items-center gap-2">
                              <Check className="h-4 w-4 text-primary" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {/* Duration Selection */}
                <div className="mt-6">
                  <Label className="mb-2 block">Durata abbonamento</Label>
                  <Select value={String(selectedMonths)} onValueChange={(v) => setSelectedMonths(parseInt(v))}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 mese</SelectItem>
                      <SelectItem value="3">3 mesi</SelectItem>
                      <SelectItem value="6">6 mesi</SelectItem>
                      <SelectItem value="12">12 mesi (risparmia 15%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Total */}
                <div className="mt-6 p-4 bg-muted rounded-lg flex justify-between items-center">
                  <span className="font-medium">Totale</span>
                  <span className="text-2xl font-black font-mono">{formatCurrency(calculateTotal())}</span>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="btn-primary">
                Continua
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Billing Info */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Dati di Fatturazione</CardTitle>
                <CardDescription>Inserisci i dati per la fatturazione elettronica</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Ragione Sociale *</Label>
                    <Input
                      value={billingInfo.company_name}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Nome Azienda Srl"
                    />
                  </div>
                  <div>
                    <Label>Partita IVA *</Label>
                    <Input
                      value={billingInfo.vat_number}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, vat_number: e.target.value }))}
                      placeholder="IT12345678901"
                    />
                  </div>
                  <div>
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={billingInfo.fiscal_code || ""}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, fiscal_code: e.target.value }))}
                      placeholder="RSSMRA80A01H501U"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Indirizzo *</Label>
                    <Input
                      value={billingInfo.address}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Via Roma 1"
                    />
                  </div>
                  <div>
                    <Label>Città *</Label>
                    <Input
                      value={billingInfo.city}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Milano"
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={billingInfo.province}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, province: e.target.value }))}
                      placeholder="MI"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label>CAP *</Label>
                    <Input
                      value={billingInfo.cap}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, cap: e.target.value }))}
                      placeholder="20100"
                    />
                  </div>
                  <div>
                    <Label>PEC *</Label>
                    <Input
                      type="email"
                      value={billingInfo.pec}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, pec: e.target.value }))}
                      placeholder="azienda@pec.it"
                    />
                  </div>
                  <div>
                    <Label>Codice SDI</Label>
                    <Input
                      value={billingInfo.sdi_code}
                      onChange={(e) => setBillingInfo(prev => ({ ...prev, sdi_code: e.target.value }))}
                      placeholder="M5UXCR1 o 0000000"
                      maxLength={7}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Indietro
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                className="btn-primary"
                disabled={!billingInfo.company_name || !billingInfo.vat_number || !billingInfo.address || !billingInfo.city || !billingInfo.cap || !billingInfo.pec}
              >
                Continua
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Metodo di Pagamento</CardTitle>
                <CardDescription>Scegli come pagare</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer ${paymentMethod === "stripe" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value="stripe" id="stripe" />
                    <Label htmlFor="stripe" className="flex items-center gap-3 cursor-pointer flex-1">
                      <CreditCard className="h-5 w-5" />
                      <div>
                        <p className="font-medium">Carta di Credito/Debito</p>
                        <p className="text-sm text-muted-foreground">Visa, Mastercard, American Express</p>
                      </div>
                    </Label>
                  </div>
                  <div className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer ${paymentMethod === "paypal" ? "border-primary bg-primary/5" : "border-border"}`}>
                    <RadioGroupItem value="paypal" id="paypal" />
                    <Label htmlFor="paypal" className="flex items-center gap-3 cursor-pointer flex-1">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z"/>
                      </svg>
                      <div>
                        <p className="font-medium">PayPal</p>
                        <p className="text-sm text-muted-foreground">Paga con il tuo account PayPal</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>

                {/* Order Summary */}
                <div className="border-t pt-6">
                  <h4 className="font-bold mb-4">Riepilogo Ordine</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Piano</span>
                      <span>{plans[selectedPlan]?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Durata</span>
                      <span>{selectedMonths} {selectedMonths === 1 ? "mese" : "mesi"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prezzo mensile</span>
                      <span>{formatCurrency(plans[selectedPlan]?.price || 0)}</span>
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                      <span>Totale</span>
                      <span className="font-mono">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PayPal Buttons */}
            {showPaypalButtons && paypalClientId && (
              <Card>
                <CardHeader>
                  <CardTitle>Completa il pagamento con PayPal</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Importo: {formatCurrency(paypalAmount)}
                  </p>
                </CardHeader>
                <CardContent>
                  <PayPalCheckoutButtons
                    clientId={paypalClientId}
                    amount={paypalAmount}
                    currency="EUR"
                    onSuccess={async (details) => {
                      try {
                        const headers = token ? { Authorization: `Bearer ${token}` } : {};
                        await axios.post(`${API}/checkout/paypal/capture`, {
                          transaction_id: paypalTransactionId,
                          paypal_order_id: details.id
                        }, { headers, withCredentials: true });
                        toast.success("Pagamento completato!");
                        navigate("/company/abbonamento?status=success");
                      } catch (err) {
                        toast.error("Errore nel completamento");
                      }
                    }}
                    onError={(err) => {
                      console.error("PayPal Error:", err);
                      toast.error("Errore PayPal");
                    }}
                    onCancel={() => {
                      toast.info("Pagamento annullato");
                      setShowPaypalButtons(false);
                    }}
                  />
                  <div className="mt-6 pt-4 border-t text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowPaypalButtons(false)}
                    >
                      Annulla
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => { setStep(2); setShowPaypalButtons(false); }}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Indietro
              </Button>
              {!showPaypalButtons && (
                <Button 
                  onClick={handleCheckout} 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {paymentMethod === "paypal" ? "Procedi con PayPal" : `Paga ${formatCurrency(calculateTotal())}`}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
